import type { HeaderNavArt } from "@noiman/ui/page-header";
import { LEARN_HEADS } from "./learn-heads.generated";
import { PUBLIC_ART_BANDS } from "../public-art/public-art-bands";

/*
帯の窓 (IT資格、面接対策、転職) と引き出しの項目に添える絵。キーは headerNav.ts の項目の id。
2026-10-26 の代表の言葉「これ nav 上でも ビジュアルあったらわかりやすいのに」。

絵は描かない。押した先の頁の帯 (lib/public-page-band.ts) か、その頁の札で使っている今ある絵を選び、
押す前と押した先で見た目がつながるようにする。帯の絵は横 4 対 1 前後なので、窓の 2 対 1 の枠では
position で主役 (人物や物) のある所を切り抜く。

帯が同じ頁どうし (技術面接とコーディング面接はどちらも interview の帯、転職診断は /career-change の帯の下) は、
同じ絵が 2 つ並ぶと見分けられないので、片方をその頁の札の絵にした。
- コーディング面接は、頁の問いの札の絵 (pages/interview-questions/coding の coding-*-tile)
- 転職診断は、診断の結果の頁の札と検索の結果の絵 (interview-career-tile)

帯の絵は暗い版を持たない (頁でも明るい版の帯を敷いている) ので、暗い版の帯でも同じ絵のまま。
site-scenes の札の絵だけ暗い版がある
*/
const scene = (id: string): HeaderNavArt => ({ src: `/site-scenes/${id}-light.svg`, darkSrc: `/site-scenes/${id}-dark.svg` });

/** 未経験からの転職ロードマップ (/career-change) の帯。lib/public-page-band.ts の BY_SECTION["/career-change"] と同じ絵 */
export const CAREER_CHANGE_BAND_SRC = "/assets/page-art/career-change-band-1120-6acec70b25.webp";

/*
セキュリティ (2026-10-26)。/security は別の Worker (yumesaku-security) が出す別アプリなので、絵も
apps/security-astro/public/fig の本物の画像を apps/client/public/assets/security-nav へそのままコピーして使う
(描き直さない)。cross-worker で直接読むと手元の開発サーバーにプロキシが無く絵が出ないための置き場。
学ぶ順は入口のホームの帯 (fig/band-light.webp)、攻撃の種類は学ぶ順の地図の「攻撃」トラックの札の絵 (fig/cards/track-offense)。
事例とハッキングを合法に学ぶは、2 対 1 の窓に合う専用の画像が今の /security に無いので絵を付けない。
窓の下の「最初のレッスンを読む」行 (menu.more) は、IT資格の「すべての資格を見る」と同じで絵を持たない行
(NavMenuPanel が more には絵を描かない) なので、ここにも足さない
*/
const SECURITY_NAV_ART_DIR = "/assets/security-nav";

export const HEADER_NAV_ART: Readonly<Record<string, HeaderNavArt>> = {
  // セキュリティ
  "security-learn": { src: `${SECURITY_NAV_ART_DIR}/security-band-light.webp`, darkSrc: `${SECURITY_NAV_ART_DIR}/security-band-dark.webp`, position: "30% 50%" },
  "security-attacks": { src: `${SECURITY_NAV_ART_DIR}/security-attacks-light.svg`, darkSrc: `${SECURITY_NAV_ART_DIR}/security-attacks-dark.svg` },
  // 転職
  "career-diagnosis": scene("interview-career-tile-v1"),
  "career-roadmap": { src: CAREER_CHANGE_BAND_SRC, position: "62% 50%" },
  jobs: { src: PUBLIC_ART_BANDS["jobs-detail"].sp, position: "72% 50%" },
  // キャリアの歩き方はコースの頁。コースの札と同じサムネ (2 対 1 に近いので切り抜きはほぼ無い)
  career: { src: "/course-thumb/career-path-guide-2026-10d.webp" },
  // 面接対策
  interview: { src: LEARN_HEADS.interview.sp, position: "6% 50%" },
  coding: scene("coding-two-pointers-tile-v1"),
  // IT資格。各試験の頁の帯 (lib/certification.ts の art.band) と同じ絵
  "certification-it-passport": { src: LEARN_HEADS["it-passport"].sp, position: "45% 50%" },
  "certification-fe": { src: LEARN_HEADS["fe-exam"].sp, position: "45% 50%" },
  "certification-linuc": { src: LEARN_HEADS.linux.sp, position: "40% 50%" },
  "certification-aws-clf": { src: LEARN_HEADS.aws.sp, position: "45% 50%" },
  "certification-sg": { src: LEARN_HEADS.security.sp, position: "45% 50%" },
  // 学ぶ。ゆめさく CS の入口と道の頁の帯 (lib/public-page-band.ts の "/computer-science" と同じ cs の絵)。
  // 引き出しの「学ぶ」組の素の 1 行 (COMPUTER_SCIENCE_NAV) と、帯の窓の「学ぶ順」行は同じ id の絵を見ないので、両方に渡す
  "computer-science": { src: LEARN_HEADS.cs.sp, position: "70% 50%" },
  "computer-science-learn": { src: LEARN_HEADS.cs.sp, position: "70% 50%" },
  // アルゴリズムとデータ構造は、道の札自身の絵 (CS_TRACKS の algorithms の art と同じ scene)
  "computer-science-algorithms": scene("coding-binary-search-tile-v1"),
  // ゆめさく数学 (/maths)。分野の札と同じ色替えの場面 (scripts/build-maths-scenes.mjs)
  maths: scene("maths-field-functions"),
  // エンジニアの英語 (/english)。分野の札と同じ色替えの場面 (scripts/build-english-scenes.mjs)
  english: scene("english-field-docs"),
  // 分野の地図と実例は、頁の中の節であって帯や札の絵を持たないので、2 対 1 の窓に合う絵が無く付けない
};
